import { Button } from '@design/components/button/Button';
import { Dropdown } from '@design/components/dropdown/Dropdown';
import {
  DropdownItem,
  DropdownListItem,
} from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { keyboard } from '@helpers/constants/keyboard';
import clsx from 'clsx';
import React, {
  ButtonHTMLAttributes,
  FocusEvent,
  ReactNode,
  useState,
} from 'react';

import styles from './PillDropdown.module.scss';

/** Shape of a single item rendered inside the pill's dropdown. */
export interface Item extends DropdownItem {
  /** Visible label. */
  label: string;
  /** Called with the pill's `value` when the item is selected. */
  onClick: (value: string) => void;
  /** Test selector forwarded as `data-testid` on the dropdown item. */
  dataTestId?: string;
}

export interface PillDropdownProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visible label rendered inside the pill. */
  label: ReactNode;
  /** Underlying value of the pill. Passed to every dropdown item's `onClick` and to `onClickPill`. */
  value?: string;
  /** Called when the pill body is clicked (independent of the dropdown). */
  onClickPill?: (value: string) => void;
  /** Optional icon renderer placed before the label. */
  renderIcon?: () => ReactNode;
  /** Items rendered when the dropdown opens. When empty (or omitted), the pill behaves as a non-dropdown button. */
  dropdownItems?: Item[];
  /** Visual variant: `'default'`, `'outline'`, or `'buttonPrimary'`. Defaults to `'default'`. */
  variant?: 'default' | 'outline' | 'buttonPrimary';
  /** Show the trailing `⋯` (more) button that toggles the dropdown. Defaults to `true`. */
  shouldShowMoreButton?: boolean;
}

/**
 * Pill-styled trigger that toggles a dropdown menu of options.
 *
 * The pill body itself is clickable (via `onClickPill`), and a
 * trailing `⋯` button opens the dropdown. Use for compact filter /
 * action affordances inline with content (e.g. table cells, chat
 * inputs).
 */
export const PillDropdown = ({
  label,
  value = '',
  renderIcon,
  dropdownItems = [],
  onClickPill,
  variant = 'default',
  shouldShowMoreButton = true,
  ...rest
}: PillDropdownProps) => {
  const [showOptions, setShowOptions] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent, item?: Item | null) => {
    if (
      event.key === keyboard.enter.key ||
      event.code === keyboard.space.code
    ) {
      event.preventDefault();
      updateShowOptions(!showOptions);
      item?.onClick?.(value);
      onClickPill?.(value);
    }
  };

  const handleBlur = ({
    currentTarget,
    relatedTarget,
  }: FocusEvent<HTMLDivElement>) => {
    if (currentTarget?.contains(relatedTarget)) {
      return;
    }

    updateShowOptions(false);
  };

  const updateShowOptions = (value: boolean) => {
    if (dropdownItems.length) {
      setShowOptions(value);
    }
  };

  const isInteractive = !!dropdownItems.length || !!onClickPill;

  return (
    <div
      onBlur={handleBlur}
      data-pill-container
      className={styles.styledPillContainer}
    >
      <Dropdown<Item>
        className={styles.styledPillDropdown}
        dropdownItems={dropdownItems}
        activeItemIdx={0}
        shouldShowItems={showOptions}
        onItemsShowChange={value => updateShowOptions(value)}
        onChildKeyDown={handleKeyDown}
        shouldCloseOnOutsideClick
        strategy="fixed"
        placement="right-start"
        offsetValue={{ x: 30, y: -30 }}
        renderDropdownItem={(item, _, isFocused) => (
          <DropdownListItem
            className={styles.styledDropdownListItem}
            data-testid="pill-dropdown-item"
            label={item.label}
            tabIndex={0}
            onKeyDown={event => handleKeyDown(event, item)}
            onClick={() => item.onClick?.(value)}
            supportiveText={item.supportiveText}
            isActive={false}
            isFocused={isFocused}
            separated={item.separated}
          />
        )}
        onClick={() => onClickPill?.(value)}
      >
        <div
          className={clsx(styles.pillDropdownRoot, styles[variant], {
            [styles.interactive]: isInteractive,
            [styles.hasOptions]: !!dropdownItems.length,
          })}
          tabIndex={-1}
          data-testid="pill-dropdown-label"
          {...(isInteractive && {
            'data-testid': 'pill-dropdown-button',
            role: 'button',
            tabIndex: 0,
            onKeyDown: handleKeyDown,
          })}
        >
          <span data-pill-label className={styles.label}>
            {renderIcon?.()}
            {label}
          </span>
          {shouldShowMoreButton && (
            <Button
              className={styles.showMoreButton}
              data-testid="pill-options-icon"
              iconName="Ellipsis"
              variant="empty"
              size="xsmall"
              {...rest}
            />
          )}
        </div>
      </Dropdown>
    </div>
  );
};
